import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ConnectionAuthKind, type IModel, type ITridentResource } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { AmazonRedshiftLinkedService } from 'app/model/linkedservice/amazon-redshift.linkedservice';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import {
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import {CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AmazonRedshiftLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                server: '*',
                database: '*',
                port: '*',
                username: '*',
                password: 'secret',
                encryptedCredential: '*',
                sslmode: '*',
                sslinsecure: '*'
            },
            parameters: '*'
        }
    }
})
export class AmazonRedshiftLinkedServiceAdapter extends LinkedServiceAdapter<AmazonRedshiftLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Partial<Model.AmazonRedshiftLinkedServiceTypeProperties> => {
        const credentialType = tridentResource.credentialDetails.credentialType;
        switch (credentialType) {
            case ConnectionAuthKind.Basic:
                const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(
                    tridentResource.datasourceReference
                );
                const path = datasourceReference.path;
                const [server, database] = path.split(';');
                return {
                    server,
                    database
                };
            default:
                return undefined;
        }
    };

    protected modelConstructor(): new () => AmazonRedshiftLinkedService {
        return AmazonRedshiftLinkedService;
    }

    protected model(): IModel {
        return AmazonRedshiftLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: AmazonRedshiftLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AmazonRedshiftLinkedService;

        properties.type = Metamodel.modelOf(linkedService).name;
        properties.typeProperties = {
            server: this.valueToResource(linkedService, AmazonRedshiftLinkedService.SERVER_NAME),
            database: this.valueToResource(linkedService, AmazonRedshiftLinkedService.DATABASE_NAME),
            port: this.valueToResource(linkedService, AmazonRedshiftLinkedService.PORT) || '5439',
            username: this.valueToResource(linkedService, AmazonRedshiftLinkedService.USER_NAME),
            password: parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                AmazonRedshiftLinkedService.PASSWORD,
                this.context
            )
        };
        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            properties.typeProperties.sslmode = this.valueToResource(linkedService, AmazonRedshiftLinkedService.SSL_MODE);
            properties.typeProperties.sslinsecure = this.valueToResource(linkedService, AmazonRedshiftLinkedService.SSL_INSECURE);
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: AmazonRedshiftLinkedService
    ): Promise<AmazonRedshiftLinkedService> {
        const properties = linkedServiceResource.properties as Model.AmazonRedshiftLinkedService;
        
        linkedService.version = properties.version === CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1;
        if (__IS_FABRIC_DPE__) {
            linkedService.version = driverVersions.v2;
        }
        if (linkedService.version !== driverVersions.v2) {
            linkedService.isLegacyConnector = true;
        }
        
        ValueAdapter.toModel(linkedService, AmazonRedshiftLinkedService.SERVER_NAME, properties.typeProperties.server);
        ValueAdapter.toModel(linkedService, AmazonRedshiftLinkedService.PORT, properties.typeProperties.port);
        ValueAdapter.toModel(
            linkedService,
            AmazonRedshiftLinkedService.DATABASE_NAME,
            properties.typeProperties.database
        );
        ValueAdapter.toModel(linkedService, AmazonRedshiftLinkedService.USER_NAME, properties.typeProperties.username);
        ValueAdapter.toModel(linkedService, AmazonRedshiftLinkedService.SSL_MODE, properties.typeProperties.sslmode);
        ValueAdapter.toModel(linkedService, AmazonRedshiftLinkedService.SSL_INSECURE, properties.typeProperties.sslinsecure);
        parameterizableSecretBaseToModel(
            linkedService,
            AmazonRedshiftLinkedService.PASSWORD,
            properties.typeProperties.password, this.context
        );

        return linkedService;
    }
}
